import { HOST, AXON_EDC_URL } from '@env';
import { Dictionary, isArray, random } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
  timer,
  EMPTY,
  ReplaySubject,
} from 'rxjs';
import {
  catchError,
  delay,
  finalize,
  retryWhen,
  share,
  switchMap,
  take,
  takeUntil,
  tap,
  map,
  filter,
} from 'rxjs/operators';
import * as uuid from 'uuid';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import type { CustomActionMap } from '@lib/data-dog-rum/custom-action-map';

import { FsAxonDevice, SnCamera, SnMapView, SnUserSettings } from '@lib/api/entities';
import { SnMapViewsService } from '@lib/api/services';
import { SnCurrentUser, SnSessionService } from '@lib/auth/services';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnSmartBadgeService } from '@lib/ui/badge';

import { AxonDevicePopoutComponent } from '../components';
import { AxonLoginToastComponent } from '../components/axon-login-toast/axon-login-toast.component';
import { DeviceNotStreamableReason } from '../enums';
import { IDeviceMarker } from '../interfaces';
import { IDevice } from '../interfaces/device.interface';
import {
  AlertCategory,
  AxonUsherAlertModel,
  AxonAlertModel,
  AxonEventModel,
  AxonDeviceModels,
} from '../models';
import {
  logPresencePollingStopped,
  PresencePollingStoppedReason,
} from '../utils/presence-logging.utils';
import { AxonConnectIntegrationService } from './axon-connect-integration.service';

interface IActiveIFrames {
  iFrame: HTMLIFrameElement;
  axonDeviceId: string;
  partnerId: string;
}

interface IToastData {
  alert: AxonUsherAlertModel | AxonAlertModel;
  followMode: boolean;
}

type AxonAuthToken = SnUserSettings['authTokens']['axonconnect'];

const deviceModelsOnMap: string[] = [
  AxonDeviceModels.AxonBody3,
  AxonDeviceModels.AxonBody4,
  AxonDeviceModels.AxonBodyWorkforce,
  AxonDeviceModels.AxonBodyWorkforceMini,
  AxonDeviceModels.AxonFleet3Hub,
  AxonDeviceModels.AxonOutpost,
  AxonDeviceModels.AxonLightpost,
];

type AnalyticsActionMap = CustomActionMap & {
  notification_shown_to_user: {
    notification: AxonUsherAlertModel;
  };
  axon_toast_docked_camera: {
    dockedCamera: AxonUsherAlertModel;
  };
  axon_toast_docked_nearby_cameras: {
    dockedNearbyCameras: AxonUsherAlertModel;
  };
  axon_view_on_map_clicked_drawer: {
    viewOnMapClicked: FsAxonDevice;
  };
  axon_camera_view_stream_drawer: {
    viewedStream: FsAxonDevice;
  };
  axon_camera_dock_stream_drawer: {
    dockedCamera: FsAxonDevice;
  };
  docked_axon_camera_unlocked: {
    camera: SnCamera;
  };
  docked_axon_camera_locked: {
    camera: SnCamera;
  };
  axon_marker_clicked: {
    clickedAxonMarker: FsAxonDevice;
  };
  axon_popout_to_docked: {
    dockedCamera: IDeviceMarker;
  };
  axon_livestream_invoked: {
    device: FsAxonDevice;
  };
  axon_docked_to_popout: {
    axonDevice: FsAxonDevice;
  };
};

export type AxonLogoutReason =
  | 'deviceSearchFailed'
  | 'refreshFailedOnce'
  | 'refreshFailedTooManyTimes'
  | 'appBarActionClicked'
  | 'axonLayerEnabled';

type MinimalDeviceData = {
  axonDeviceId: string;
};

@Injectable()
export class FsAxonService {
  get isAuth$(): Observable<boolean> {
    return this._isAuth$.asObservable();
  }

  get toastClicked$(): Observable<IToastData> {
    return this._toastClicked$.asObservable();
  }

  get axonMapView$(): Observable<FsAxonService> {
    return this._axonMapView$.asObservable();
  }

  get authChanged$(): Observable<AxonAuthToken | null> {
    return this._authChanged$.asObservable();
  }

  get newAlertMessage$(): Observable<AxonUsherAlertModel> {
    return this._newAlertMessage$.asObservable();
  }

  get devicesUpdated$(): Observable<IDeviceMarker[]> {
    return this._deviceUpdated$.asObservable();
  }

  /**
   * To prevent duplicate calls to DSA from both Map and the Axon device drawer, only Map
   * will do the GET request.
   *
   * The response is stored in this service, which is injected into both the map and drawer.
   */
  get deviceStatusMap$(): Observable<Dictionary<FsAxonDevice[]>> {
    return this._deviceStatusMap$.asObservable();
  }

  get updateParams$(): Observable<any> {
    return this._updateParams$.asObservable();
  }

  get bodyCameraDocked$(): Observable<any> {
    return this._bodyCameraDocked$.asObservable();
  }

  get bodyCameraDockedByMessage$(): Observable<any> {
    return this._bodyCameraDockedByMessage$.asObservable();
  }

  get nearbyCamerasDocked$(): Observable<any> {
    return this._nearbyCamerasDocked$.asObservable();
  }

  get deviceMap$(): Observable<Map<string, FsAxonDevice>> {
    return this._deviceMap$.asObservable();
  }

  get enterFollowMode$(): Observable<IDevice> {
    return this._enterFollowMode$.asObservable();
  }

  get leaveFollowMode$(): Observable<IDevice> {
    return this._leaveFollowMode$.asObservable();
  }

  get iFrameFM$(): Observable<IDevice> {
    return this._iFrameFM$.asObservable();
  }

  get openDeviceHistory$(): Observable<IDevice> {
    return this._openDeviceHistory$.asObservable();
  }

  get openPopout$(): Observable<MinimalDeviceData> {
    return this._openPopout$.asObservable();
  }

  get turnOffLayer$(): Observable<boolean> {
    return this._turnOffLayer$.asObservable();
  }

  get startSearch$(): Observable<boolean> {
    return this._startSearch$.asObservable();
  }

  get stopRefreshTimer$(): Observable<void> {
    return this._stopRefreshTimer$.asObservable();
  }

  get userInfo$(): Observable<any> {
    return this._userInfo$.asObservable();
  }

  get failedToStreamMessage$(): Observable<any> {
    return this._failedToStreamMessage$.asObservable();
  }

  get orgSpecificBaseUrl(): string {
    if (this.domain) {
      return `https://${this.domain}`;
    }

    // for isExternallyManaged users without AxonSSO, fallback to AxonConnectIntegrationService metadata
    if (this._agencyId) {
      return `https://${this._agencyId}${AXON_EDC_URL}`;
    }
  }

  get publicGatewayUrl(): string {
    if (this._baseDomain) {
      return `https://livestream.${this._baseDomain}`;
    }
    // Make this backward compatible, old versions of the settings response did not include the `baseDomain`
    if (this._regionCode) {
      return `https://livestream.${this._regionCode}.evidence.com`;
    }
    // Legacy fallback behaviour, the `regionCode` was null for PROD
    return 'https://livestream.evidence.com';
  }

  // Serving user-facing APIs - including with presence + alerts
  get rtoApiGatewayUrl(): string {
    if (this._baseDomain) {
      return `https://api-rt.${this._baseDomain}`;
    }
    // Make this backward compatible, old versions of the settings response did not include the `baseDomain`
    if (this._regionCode) {
      return `https://api-rt.${this._regionCode}.evidence.com`;
    }
    // Legacy fallback behaviour, the `regionCode` was null for PROD
    return 'https://api-rt.evidence.com';
  }

  private get _shouldRefresh(): boolean {
    return (
      this._currentUser.isDeviceLayerOn ||
      this._currentUser.settings?.authTokens?.axonconnect?.isSso
    );
  }

  set deviceStatusMap(map: Dictionary<FsAxonDevice[]>) {
    this._deviceStatusMap$.next(map);
  }

  private _userInfoRequest$: Observable<any> | null = null;

  public activeIFrames: IActiveIFrames[] = [];
  public watchMeIFrame: HTMLIFrameElement;

  public deviceInFollowMode: IDevice;

  public activePopouts: MatDialogRef<AxonDevicePopoutComponent>[] = [];
  public isAuthenticating: boolean;

  public get acccesTokenCreatedTimestamp(): number {
    return JSON.parse(localStorage.getItem('acccesTokenCreatedTimestamp'));
  }

  public agencyId: string;
  public isActive: boolean;
  public domain: string;

  public alertCategory: any[] = [];
  public infoCategory: any[] = [];
  public offCategory: any[] = [];

  public deviceMap: Map<string, FsAxonDevice>;
  public deviceNotificationMap: Map<string, AxonEventModel[]>;

  private _currentToken: string = '';
  private _tokenCreatedTimestampAtTimer: number;
  private _refreshTries: number = 3;
  private _command: {
    action: string;
    accessToken: string;
  };
  /**
   * Warning! This property has inconsistent behaviour when using Axon-SSO. Avoid when possible.
   * @deprecated see https://axon.quip.com/1kAeAyzjYSx7/Fusus-Axonconnect-region-setting
   */
  private _regionCode: string;

  private readonly _isAuth$ = new BehaviorSubject<boolean>(false);
  private readonly _toastClicked$ = new BehaviorSubject<IToastData>(undefined);
  private readonly _authChanged$ = new Subject<AxonAuthToken | null>();
  private readonly _newAlertMessage$ = new BehaviorSubject<AxonUsherAlertModel>(undefined);
  private readonly _deviceMap$ = new BehaviorSubject<Map<string, FsAxonDevice>>(undefined);
  private readonly _deviceStatusMap$ = new BehaviorSubject<Dictionary<FsAxonDevice[]>>(undefined);
  private readonly _deviceUpdated$ = new BehaviorSubject<IDeviceMarker[]>([]);
  private readonly _bodyCameraDockedByMessage$ = new BehaviorSubject<any>(null);
  private readonly _bodyCameraDocked$ = new BehaviorSubject<any>(null);
  private readonly _iFrameFM$ = new BehaviorSubject<IDevice>(null);
  private readonly _enterFollowMode$ = new BehaviorSubject<IDevice>(undefined);
  private readonly _leaveFollowMode$ = new BehaviorSubject<IDevice>(undefined);
  private readonly _openDeviceHistory$ = new BehaviorSubject<IDevice>(undefined);
  private readonly _openPopout$ = new BehaviorSubject<MinimalDeviceData>(undefined);
  private readonly _nearbyCamerasDocked$ = new BehaviorSubject<any>(null);
  private readonly _updateParams$ = new BehaviorSubject<any>(undefined);
  private readonly _axonMapView$ = new Subject<any>();
  private readonly _turnOffLayer$ = new Subject<boolean>();

  private readonly _startSearch$ = new BehaviorSubject<boolean>(false);
  private readonly _failedToStreamMessage$ = new Subject<any>();
  private readonly _destroy$ = new Subject<any>();
  private readonly _stopRefreshTimer$ = new Subject<void>();
  private _refreshRequest$: Subscription;
  private _baseDomain?: string;
  private _agencyId?: string;

  // User information variables
  private _userInfo$ = new BehaviorSubject<any>(null);

  constructor(
    private readonly _currentUser: SnCurrentUser,
    private readonly _httpClient: HttpClient,
    private readonly _smartBadge: SnSmartBadgeService,
    private readonly _mapViewService: SnMapViewsService,
    private readonly _toastrService: ToastrService,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _sessionService: SnSessionService,
    private readonly _axonIntegrationService: AxonConnectIntegrationService,
  ) {
    this._sessionService.loggedIn$.pipe(takeUntil(this._destroy$)).subscribe((loggedIn) => {
      if (!loggedIn) {
        this._destroy();
      }
    });

    this._regionCode = this._currentUser.settings?.authTokens?.axonconnect?.region;
    this.domain = this._currentUser.settings?.authTokens?.axonconnect?.domain;
    this._baseDomain = this._currentUser.settings?.authTokens?.axonconnect?.baseDomain;
    this._axonIntegrationService.integrationMetadata$
      .pipe(takeUntil(this._destroy$))
      .subscribe((meta) => {
        this._agencyId = meta?.agencyId;
      });

    this.authChanged$
      .pipe(
        takeUntil(this._destroy$),
        switchMap((res) => {
          if (!res) {
            this._turnOffLayer$.next(true);
            return EMPTY;
          }

          this._regionCode = res.region;
          this._baseDomain = res.baseDomain;

          return this._axonIntegrationService.integrationMetadata$.pipe(
            map((meta) => ({ res, meta })),
          );
        }),
      )
      .subscribe(({ res, meta }) => {
        // we check also for null features for backwards compatibility, these organizations have Respond but are not synced from evidence.com
        const hasOrgAxonRespondPermission = meta.features?.respond || !meta.features;
        const hasSelfLocateScope = res.scope.includes('axon_aware.self.locate');

        if (!hasSelfLocateScope || !hasOrgAxonRespondPermission) {
          this._turnOffLayer$.next(true);
          this._smartBadge.setBadge('axon-connect-status', 'x', 'danger');
          return;
        }

        this._command = {
          action: 'ACCESS_TOKEN_UPDATED',
          accessToken: res.accessToken,
        };

        if (!this.watchMeIFrame || this._smartBadge.getBadge('axon-connect-status') === 'x') {
          this.watchMeIFrame = this.generateHeadlessIFrame();
          const element = document.querySelector('.headlessIFrameContainer');
          if (element) {
            element.appendChild(this.watchMeIFrame);
          }
        } else {
          this.watchMeIFrame.contentWindow.postMessage(this._command, '*');
        }

        if (this.activeIFrames.length) {
          this.activeIFrames.forEach((active) => {
            active.iFrame.contentWindow.postMessage(this._command, '*');
          });
        }
        this.logSafeTokenDetails({
          region: res.region,
          domain: res.domain,
          baseDomain: res.baseDomain,
          features: res.features,
          isSso: res.isSso,
        });
      });

    window.addEventListener('message', (e) => {
      if (e.data.message === 'tokenUpdated') {
        this.setAuth(e.data.token);
        this._startSearch();
      }
      if (e.data.action === 'INVALID_ACCESS_HEADLESS_MODE') {
        this.addAnalytics('axon_invalid_access_headless_mode');
      }
      if (
        e.data.message === 'insufficientScope' ||
        e.data.action === 'INVALID_ACCESS_HEADLESS_MODE'
      ) {
        this._toastrService.error(
          'Your evidence.com user does not have permission to access Axon Devices.',
          'Permission Denied',
          { disableTimeOut: true, closeButton: true },
        );
        this._turnOffLayer$.next(true);
        this.revokeToken()
          .pipe(takeUntil(this._destroy$))
          .subscribe(() => {
            this._currentUser.settings.authTokens.axonconnect = {};
            this._logPresencePollingStopped('token_revoked', this.isActive);
          });
      }
      if (e.data.action === 'WS_CONNECTION_STATE_CHANGED') {
        switch (e.data.connectionState) {
          case 'Disconnected':
            this._smartBadge.setBadge('axon-connect-status', 'x', 'danger');
            break;
          case 'UsherTokenPending':
          case 'UsherSocketPending':
            this._smartBadge.setBadge('axon-connect-status', '...', 'warning');
            break;
          case 'Connected':
            this._smartBadge.setBadge('axon-connect-status', '✔', 'success');
            break;
        }
      }

      if (e.data.action === 'NOTIFICATION_RECEIVED' && e.data.notification && this.isActive) {
        const message = new AxonUsherAlertModel(e.data.notification);
        this._newAlertMessage$.next(message);
        this._datadogService.addAction('notification_received_from_axon', e.data);
      }

      if (e.data.message === 'disclaimerDeclined') {
        this._turnOffLayer$.next(true);
        this.isAuthenticating = false;
      }
    });
  }

  public setAuth(auth: AxonAuthToken, updatedFromMain: boolean = false): void {
    if (auth.accessToken !== this._currentToken) {
      if (updatedFromMain) {
        const date = Date.now();
        localStorage.setItem('acccesTokenCreatedTimestamp', JSON.stringify(date));
        this._tokenCreatedTimestampAtTimer = date;
      }
      this._currentToken = auth.accessToken;
      const previousIsActive = this.isActive;
      this.isActive = this._currentUser.isDeviceLayerOn;
      if (previousIsActive !== this.isActive) {
        this._logPresencePollingStopped('isActive_toggled', previousIsActive);
      }
      // refresh the users token if they are using the Axon Devices layer or Axon as their SSO provider
      if (this._shouldRefresh) {
        this.startRefreshTimer();
      }
      this._authChanged$.next(auth);
    }
  }

  public updateParams(params: any): void {
    this._updateParams$.next(params);
  }

  public axonMapView(mapView: any): void {
    this._axonMapView$.next(mapView);
  }

  public addAnalytics<T extends keyof AnalyticsActionMap>(
    message: T,
    data?: AnalyticsActionMap[T],
  ): void {
    this._datadogService.addAction(message, data);
  }

  public filterForMap(devices: FsAxonDevice[]): FsAxonDevice[] {
    return devices.filter((device) => {
      return (
        deviceModelsOnMap.includes(device.deviceModel) ||
        deviceModelsOnMap.includes(device.deviceModelInternal)
      );
    });
  }

  public openAxonAuthenticationOrLogOut({ reason }: { reason: AxonLogoutReason }): void {
    if (!this._currentUser.id) {
      return;
    }
    if (!this.isAuthenticating) {
      this.isAuthenticating = true;
      const isSignedInWithSso = this._sessionService.isAxonSso;
      if (isSignedInWithSso) {
        this._logUserOut({ reason });
      } else {
        this._openAxonConnectInNewWindow();
      }
    }
  }

  public refreshOnce(): void {
    this._logAxonRefreshAttempt({ retryBased: false });

    this._httpClient
      .get('/api/integrations/axon/connect/oauth/refresh/')
      .pipe(
        tap({
          next: () => {
            this.getUpdatedUser();
            localStorage.setItem(
              'acccesTokenCreatedTimestamp',
              JSON.stringify(new Date().getTime()),
            );
            this._logAxonRefreshSuccess({ retryBased: false });
          },
          error: (error) => {
            this._logAxonRefreshFailed({ error, retryBased: false });
            this.openAxonAuthenticationOrLogOut({
              reason: 'refreshFailedOnce',
            });
          },
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public authenticateAxon(): void {
    this._getIDPConfig();
  }

  public permissionMissing(reason: string, deviceId: string): void {
    let message;
    switch (reason) {
      case DeviceNotStreamableReason.ExternalFeatureFlag:
      case DeviceNotStreamableReason.RespondFeatureFlag:
      case DeviceNotStreamableReason.LackingCorrectScope:
      case DeviceNotStreamableReason.NotInCommandHierarchy:
      case DeviceNotStreamableReason.DeviceLivestreamSettings:
        message = "You don't have permission to view this livestream";
        break;
      case DeviceNotStreamableReason.ExternalNotStreamable:
      case DeviceNotStreamableReason.StaleState:
      case DeviceNotStreamableReason.InsufficientSignal:
      case DeviceNotStreamableReason.LackingConnectedDevices:
        message = 'Livestreaming is not available';
        break;
      case DeviceNotStreamableReason.Offline:
      case DeviceNotStreamableReason.DeviceNotRecording:
        message = 'Camera must be recording to start livestreaming';
        break;
    }

    const data = {
      message,
      deviceId,
    };

    this._failedToStreamMessage$.next(data);
  }

  public callback(queryParams: { code: string; state: string }): Observable<any> {
    let redirectURI;
    if (HOST.toString() === 'localhost:8000') {
      redirectURI = 'http://localhost:8000/oauth/axonrespond';
    }
    if (HOST.toString() === 'stage.fususone.com') {
      redirectURI = 'https://stage.fususone.com/oauth/axonrespond';
    }

    return this._httpClient.get('/api/integrations/axon/connect/oauth/callback/', {
      params: {
        code: queryParams.code,
        state: queryParams.state,
        environment: 'dashboard',
        redirect_uri: redirectURI,
      },
      headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
    });
  }

  public dockBodyCameraByMessage(message: AxonUsherAlertModel | AxonAlertModel | any): void {
    let normalizedMessage: any;

    // Check if it's an AxonAlertModel and normalize the structure
    if (message instanceof AxonAlertModel || (message.enrichments && message.enrichments.device)) {
      normalizedMessage = {
        device: {
          deviceId: message.enrichments.device.id,
          signalType: 'Unknown', // Not available in new format
          signalStrength: 'Unknown', // Not available in new format
        },
        notification: {
          deviceId: message.enrichments.device.id,
          deviceModel: message.enrichments.device.model,
        },
        // Include original message for additional data if needed
        originalMessage: message,
        messageType: 'AxonAlertModel',
      };
    }
    // For AxonUsherAlertModel or legacy format, pass through as-is
    else {
      normalizedMessage = {
        ...message,
        messageType: 'AxonUsherAlertModel',
      };
    }

    this._bodyCameraDockedByMessage$.next(normalizedMessage);
  }

  public createTab(message: AxonUsherAlertModel | AxonAlertModel | any): void {
    let longitude: number;
    let latitude: number;
    let externalId: string;
    let name: string;

    if (message instanceof AxonAlertModel) {
      externalId = message.enrichments.device.id;
      name = `${message.ownerFullName || 'Unknown Owner'} Livestream`;

      const deviceStatusMap = this._deviceStatusMap$.value;
      if (deviceStatusMap && externalId) {
        const allDeviceArrays = Object.values(deviceStatusMap);
        for (const deviceArray of allDeviceArrays) {
          const fsAxonDevice = deviceArray.find(
            (device) => device.axonDeviceId === externalId || device.id === externalId,
          );
          if (fsAxonDevice) {
            if (
              fsAxonDevice.attributes?.location?.longitude &&
              fsAxonDevice.attributes?.location?.latitude
            ) {
              longitude = fsAxonDevice.attributes.location.longitude;
              latitude = fsAxonDevice.attributes.location.latitude;
            }
            break;
          }
        }
      }
    } else if (message instanceof AxonUsherAlertModel) {
      longitude = message.device.deviceLocation.longitude;
      latitude = message.device.deviceLocation.latitude;
      externalId = message.device.deviceId;
      name = `${this._reverseName(message.ownerName)} Livestream`;
    }

    if (longitude !== undefined && latitude != undefined) {
      this._mapViewService
        .createMapView(
          new SnMapView({
            lon: longitude,
            lat: latitude,
            isTemporary: true,
            externalId: externalId,
            name: name,
          }),
        )
        .subscribe((mapView) => {
          this._axonMapView$.next(mapView);
        });
    }
  }

  public dockBodyCamera(marker: IDeviceMarker): void {
    this._bodyCameraDocked$.next(marker);
  }

  public dockNearbyCameras(message: AxonUsherAlertModel | AxonAlertModel | any): void {
    let normalizedMessage: any;

    // Check if it's an AxonAlertModel and normalize the structure
    if (message instanceof AxonAlertModel || (message.enrichments && message.enrichments.device)) {
      normalizedMessage = {
        device: {
          deviceId: message.enrichments.device.id,
          signalType: 'Unknown', // Not available in new format
          signalStrength: 'Unknown', // Not available in new format
        },
        notification: {
          deviceId: message.enrichments.device.id,
          deviceModel: message.enrichments.device.model,
        },
        // Include original message for additional data if needed
        originalMessage: message,
        messageType: 'AxonAlertModel',
      };
    }
    // For AxonUsherAlertModel or legacy format, pass through as-is
    else {
      normalizedMessage = {
        ...message,
        messageType: 'AxonUsherAlertModel',
      };
    }

    this._nearbyCamerasDocked$.next(normalizedMessage);
  }

  public getOrFetchUserInfo(): Observable<any> {
    if (this._userInfo$.value) {
      return of(this._userInfo$.value);
    }

    if (this._userInfoRequest$) {
      return this._userInfoRequest$;
    }

    const tokenError = this.validateToken();
    if (tokenError) {
      return throwError(tokenError);
    }

    this._userInfoRequest$ = this.getAxonUserInformation().pipe(
      tap((userInfo) => {
        if (userInfo?.agencyId) {
          this.agencyId = userInfo.agencyId;
          this.receiveEvidenceSettings(userInfo.agencyDomain);
        }
        this._userInfo$.next(userInfo);
      }),
      finalize(() => {
        this._userInfoRequest$ = null;
      }),
      share(),
    );

    return this._userInfoRequest$;
  }

  public validateToken(): Error | void {
    const axonToken = this._currentUser.settings?.authTokens?.axonconnect;
    if (!axonToken?.accessToken) {
      const validationError = 'No Axon Connect token available';
      this._logAxonValidationFailed({ validationError });
      return new Error(validationError);
    }

    // Check if token has expired using obtained timestamp + expiresIn duration
    if (axonToken.expiresIn && axonToken.obtained) {
      const now = Date.now();
      const obtainedAtTimestamp = new Date(axonToken.obtained).getTime();
      const expirationTime = obtainedAtTimestamp + axonToken.expiresIn * 1000;

      if (now > expirationTime) {
        const validationError = 'Axon Connect token has expired';
        this._logAxonValidationFailed({ validationError });
        return new Error(validationError);
      }
    }
  }

  public fetchUserInfo(): void {
    const totalFetchAttempts = 5;
    this.getAxonUserInformation()
      .pipe(
        retryWhen((errors) => errors.pipe(delay(30000), take(totalFetchAttempts))),
        takeUntil(this._destroy$),
      )
      .subscribe((res) => {
        if (res && res.agencyId) {
          this.agencyId = res.agencyId;
          this.receiveEvidenceSettings(res.agencyDomain);
        }
      });
  }

  public refreshToken(): void {
    if (!this._shouldRefresh) {
      return;
    }
    if (this._tokenCreatedTimestampAtTimer !== this.acccesTokenCreatedTimestamp) {
      this._refreshRequest$ = null;
      // this means the token was refreshed in a different tab
      this.getUpdatedUser();
      this._tokenCreatedTimestampAtTimer = this.acccesTokenCreatedTimestamp;
      return;
    }

    if (this._refreshRequest$) {
      this.addAnalytics('axon_connect_prevented_local_race_condition');
      return;
    }

    this._logAxonRefreshAttempt({ retryBased: true });

    this._refreshRequest$ = this._httpClient
      .get('/api/integrations/axon/connect/oauth/refresh/')
      .pipe(
        retryWhen((errors) => errors.pipe(delay(30000), take(this._refreshTries))),
        catchError((err) => {
          this.isAuthenticating = false;
          this._logAxonRefreshFailed({ error: err, retryBased: true, exhaustedRetries: true });
          this.openAxonAuthenticationOrLogOut({
            reason: 'refreshFailedTooManyTimes',
          });
          return throwError(err);
        }),
        takeUntil(this._destroy$),
        finalize(() => {
          this._refreshRequest$ = null;
        }),
      )
      .subscribe(() => {
        this.getUpdatedUser();
        this._logAxonRefreshSuccess({ retryBased: true });
        localStorage.setItem('acccesTokenCreatedTimestamp', JSON.stringify(new Date().getTime()));
      });
  }

  public revokeToken(): Observable<any> {
    return this._httpClient.get('/api/integrations/axon/connect/oauth/revoke/');
  }

  public getAxonUserInformation(): Observable<any> {
    return this._httpClient.get('/api/integrations/axon/connect/oauth/userinfo/');
  }

  public enterFollowMode(device: IDevice): void {
    this.deviceInFollowMode = device;
    this._enterFollowMode$.next(device);
  }

  public leaveFollowMode(device: IDevice): void {
    this.deviceInFollowMode = undefined;
    this._leaveFollowMode$.next(device);
  }

  public iFrameFM(device: IDevice): void {
    this._iFrameFM$.next(device);
  }

  public openPopout(device: { axonDeviceId: string }): void {
    this._openPopout$.next(device);
  }

  public clickToast(
    alert: AxonUsherAlertModel | AxonAlertModel,
    followMode: boolean = false,
  ): void {
    const data = {
      alert,
      followMode,
    };
    this._toastClicked$.next(data);
  }

  public devicesUpdated(devices: IDeviceMarker[]): void {
    this._deviceUpdated$.next(devices);
  }

  public updateHeadlessIFrame(): void {
    if (this.watchMeIFrame) {
      this.watchMeIFrame.contentWindow.postMessage(this._command, '*');
    }
  }

  public getUpdatedUser(): void {
    this._currentUser
      .getSettings()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        this._refreshTries = 3;
        if (res) {
          this._currentUser.settings = res;
          const hasSelfLocate =
            this._currentUser.settings.authTokens.axonconnect?.scope?.includes(
              'axon_aware.self.locate',
            );
          if (hasSelfLocate) {
            this.setAuth(this._currentUser.settings.authTokens.axonconnect || null);
          } else {
            this.addAnalytics('axon_invalid_scope');
          }
          this._authChanged$.next(res.authTokens?.axonconnect || null);
          this._regionCode = res.authTokens?.axonconnect?.region || null;
          this.domain = res.authTokens?.axonconnect?.domain || null;
          this._baseDomain = res.authTokens?.axonconnect?.baseDomain ?? null;
          this.isAuthenticating = false;
          if (hasSelfLocate && res.authTokens?.axonconnect?.accessToken) {
            this._startSearch();
          }
        }
      });
  }

  public generateHeadlessIFrame(): HTMLIFrameElement {
    const iframeUrl = `${this.publicGatewayUrl}/livestream-player?mode=headless`;
    const iframe = document.createElement('iframe');
    iframe.style.width = '100px';
    iframe.style.height = '46px';
    iframe.style.overflow = 'none';
    iframe.style.border = 'none';
    iframe.id = uuid.v4();
    iframe.src = iframeUrl;
    iframe.onload = () => {
      const command = {
        action: 'ACCESS_TOKEN_UPDATED',
        accessToken: this._currentToken,
      };
      iframe.contentWindow.postMessage(command, '*');
    };
    return iframe;
  }

  public determineAlert(category: string): boolean {
    switch (category) {
      case AlertCategory.WatchMeActivate:
      case AlertCategory.WatchMeActivateMarked:
      case AlertCategory.Gunshot:
      case AlertCategory.CewUnholstered:
        return true;
      case AlertCategory.WatchMeCancel:
      case AlertCategory.WatchMeCancelMarked:
      case AlertCategory.Holster:
        return false;
    }
  }

  public updateDeviceNotificationMap(messages: AxonEventModel[] | AxonEventModel): {
    notifications: Map<string, AxonEventModel[]>;
  } {
    if (!this.deviceNotificationMap) {
      this.deviceNotificationMap = new Map<string, AxonEventModel[]>();
    }

    if (isArray(messages)) {
      messages.forEach((message) => {
        this._addNotificationToMap(message);
      });
    } else {
      this._addNotificationToMap(messages);
    }

    return { notifications: this.deviceNotificationMap };
  }

  public clearDeviceNotificationMap(): void {
    this.deviceNotificationMap = new Map<string, AxonEventModel[]>();
  }

  public createDeviceMap(devices: FsAxonDevice[]): void {
    const deviceMap = new Map<string, FsAxonDevice>();

    devices.forEach((device) => {
      deviceMap.set(device.axonDeviceId, device);
    });

    this.deviceMap = deviceMap;
  }

  public updateDeviceMap(devices: FsAxonDevice[]): {
    devices: Map<string, FsAxonDevice>;
    changes: string[];
  } {
    if (!this.deviceMap) {
      this.createDeviceMap(devices);
      return { devices: this.deviceMap, changes: [] };
    }
    const newDeviceMap = new Map<string, FsAxonDevice>();
    const changes: string[] = [];

    devices.forEach((device) => {
      const existingDevice = this.deviceMap.get(device.axonDeviceId);

      if (!existingDevice || JSON.stringify(existingDevice) !== JSON.stringify(device)) {
        changes.push(device.axonDeviceId);
      }

      newDeviceMap.set(device.axonDeviceId, device);
    });

    this.deviceMap = newDeviceMap;

    return { devices: this.deviceMap, changes };
  }

  public openDeviceHistory(device: IDevice): void {
    this._openDeviceHistory$.next(device);
  }

  public startRefreshTimer(): void {
    const minimumSecondsLeft = random(5, 90); // random so that one tab wins
    const startTimerAfterSeconds = random(30, 60);
    this._stopRefreshTimer$.next();
    this._tokenCreatedTimestampAtTimer = this.acccesTokenCreatedTimestamp;
    timer(startTimerAfterSeconds * 1000, 1000)
      .pipe(
        switchMap(() => {
          const now = Date.now();
          const expiresIn = this._currentUser.settings?.authTokens?.axonconnect?.expiresIn || 300;
          const timeLeft = this.acccesTokenCreatedTimestamp
            ? (now - this.acccesTokenCreatedTimestamp) / 1000
            : expiresIn;
          if (Math.ceil(expiresIn - timeLeft) <= minimumSecondsLeft) {
            this.refreshToken();
            this._stopRefreshTimer$.next();
            return of({ refresh: true });
          }
          return of({ refresh: false });
        }),
        takeUntil(this._stopRefreshTimer$),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public receiveEvidenceSettings(domain: string): void {
    const headers = {
      accept: 'application/vnd.api+json',
      'content-type': 'application/json',
      clientType: 'INTERNAL_MOBILE',
      authorization: `Bearer ${this._currentUser.settings.authTokens?.axonconnect?.accessToken}`,
    };

    const url = `${this.publicGatewayUrl}/api/v1/internal/${this.agencyId}/awareNotificationConfigs`;

    this._httpClient
      .get(url, { headers })
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        this.alertCategory = res.data
          .filter((source) => source.level === 'Alert')
          .map((s) => s.source);
        this.infoCategory = res.data
          .filter((source) => source.level === 'Info')
          .map((s) => s.source);
      });
  }

  private _addNotificationToMap(notification: AxonEventModel): void {
    if (!this.deviceNotificationMap.has(notification.attributes?.device?.deviceId)) {
      this.deviceNotificationMap.set(notification.attributes?.device?.deviceId, []);
    }

    this.deviceNotificationMap.get(notification.attributes?.device?.deviceId).push(notification);
  }

  private _getIDPConfig(): void {
    let redirectURI;
    if (HOST.toString() === 'localhost:8000') {
      redirectURI = 'http://localhost:8000/oauth/axonrespond';
    }
    if (HOST.toString().includes('stage.fususone')) {
      redirectURI = 'https://stage.fususone.com/oauth/axonrespond';
    }
    this._httpClient
      .get('/api/integrations/axon/connect/oauth/authorize/', {
        params: {
          environment: 'dashboard',
          redirect_uri: redirectURI,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: { url: string }) => {
        this._openOauthWindow(res.url);
      });
  }

  private _openOauthWindow(fullUrl: string): void {
    window.location.replace(fullUrl);
  }

  private _startSearch(): void {
    this._startSearch$.next(true);
  }

  private _reverseName(s: string): string {
    if (s) {
      return s.split(',').reverse().join().replace(',', ' ');
    }
  }

  private _openAxonConnectInNewWindow(): void {
    const newWindow = window.open(
      '/oauth/axonrespond?start=true',
      null,
      'popup=true,width=800,height=800',
    );

    newWindow.addEventListener('unload', () => {
      this.isAuthenticating = false;
    });

    if (!newWindow || newWindow.closed) {
      this.addAnalytics('axon_connect_login_toast');
      this._toastrService
        .show('Click on this notification to connect', 'Axon Evidence.com Log-in Required.', {
          toastComponent: AxonLoginToastComponent,
          disableTimeOut: true,
          closeButton: true,
          tapToDismiss: true,
        })
        .onTap.pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          const toastWindow = window.open(
            '/oauth/axonrespond?start=true',
            null,
            'popup=true,width=800,height=800',
          );

          toastWindow.addEventListener('unload', () => {
            this.isAuthenticating = false;
          });
        });
    }
  }

  private _logUserOut({ reason }: { reason: AxonLogoutReason }) {
    this._sessionService.logoutWithoutBlacklist({
      redirectToLogoutScreen: true,
      reason: `axon.service:${reason}`,
    });
  }

  private _calculateTokenExpiry(
    obtainedTimestamp: number | null,
    expiresInSeconds: number | undefined,
  ): { expired: boolean | undefined; secondsUntilExpiry: number | null } {
    if (!expiresInSeconds || !obtainedTimestamp || isNaN(obtainedTimestamp)) {
      return { expired: undefined, secondsUntilExpiry: null };
    }

    const now = Date.now();
    const expirationTime = obtainedTimestamp + expiresInSeconds * 1000;
    const expired = now > expirationTime;
    const secondsUntilExpiry = Math.floor((expirationTime - now) / 1000);

    return { expired, secondsUntilExpiry };
  }

  private _getTokenPreview(token: string | undefined): string | null {
    if (!token || typeof token !== 'string' || token.length < 4) {
      return null;
    }
    return token.slice(-4);
  }

  private _getAxonTokenContext() {
    const axonToken = this._currentUser.settings?.authTokens?.axonconnect;
    const obtainedTimestamp = axonToken?.obtained ? new Date(axonToken.obtained).getTime() : null;

    // Return early if no token or invalid timestamp (NaN)
    if (!axonToken || (obtainedTimestamp !== null && isNaN(obtainedTimestamp))) {
      return {
        axonAccessTokenPreview: axonToken ? this._getTokenPreview(axonToken.accessToken) : null,
        axonAccessTokenExpired: undefined,
        axonAccessTokenSecondsUntilExpiry: null,
        axonRefreshTokenExpired: undefined,
        axonRefreshTokenSecondsUntilExpiry: null,
      };
    }

    // Calculate access token expiry
    const {
      expired: axonAccessTokenExpired,
      secondsUntilExpiry: axonAccessTokenSecondsUntilExpiry,
    } = this._calculateTokenExpiry(obtainedTimestamp, axonToken.expiresIn);

    // Calculate refresh token expiry
    const {
      expired: axonRefreshTokenExpired,
      secondsUntilExpiry: axonRefreshTokenSecondsUntilExpiry,
    } = this._calculateTokenExpiry(obtainedTimestamp, axonToken.refreshTokenExpiresIn);

    return {
      axonAccessTokenPreview: this._getTokenPreview(axonToken.accessToken),
      axonAccessTokenExpired,
      axonAccessTokenSecondsUntilExpiry,
      axonRefreshTokenExpired,
      axonRefreshTokenSecondsUntilExpiry,
    };
  }

  private _logAxonRefreshAttempt({ retryBased }: { retryBased: boolean }): void {
    const tokenContext = this._getAxonTokenContext();

    this._datadogService.addAction('axon_token_refresh_attempt', {
      retryBased,
      ...tokenContext,
    });
  }

  private _logAxonRefreshSuccess({ retryBased }: { retryBased: boolean }): void {
    this._datadogService.addAction('axon_token_refresh_success', {
      retryBased,
    });
  }

  private _logAxonRefreshFailed({
    error,
    retryBased,
    exhaustedRetries = false,
  }: {
    error: any;
    retryBased: boolean;
    exhaustedRetries?: boolean;
  }): void {
    const tokenContext = this._getAxonTokenContext();
    const errorMessage = typeof error?.error === 'string' ? error.error : error?.message;

    this._datadogService.addAction('axon_token_refresh_failed', {
      retryBased,
      exhaustedRetries,
      statusCode: error?.status,
      errorMessage,
      errorResponse: error?.error,
      ...tokenContext,
    });
  }

  private _logAxonValidationFailed({ validationError }: { validationError: string }): void {
    const tokenContext = this._getAxonTokenContext();

    this._datadogService.addAction('axon_token_validation_failed', {
      validationError,
      ...tokenContext,
    });
  }

  private _logPresencePollingStopped(
    reason: PresencePollingStoppedReason,
    wasFetching: boolean,
  ): void {
    logPresencePollingStopped(this._datadogService, {
      service: 'map',
      mode: this._currentUser.organization?.settings?.presenceApiEnableMode,
      reason,
      wasFetching,
      organizationId: this._currentUser.organization?.id,
      agencyId: this.agencyId,
      userId: this._currentUser.id,
    });
  }

  private _destroy(): void {
    this.watchMeIFrame = null;
    this._stopRefreshTimer$.next();
  }

  private logSafeTokenDetails(
    safeTokenDetails: Pick<
      AxonAuthToken,
      'domain' | 'baseDomain' | 'region' | 'features' | 'isSso'
    >,
  ) {
    this._datadogService.setGlobalContextProperty('axon_connect', safeTokenDetails);
  }
}
